import {ChangeDetectorRef, Component, inject, ViewChild, AfterViewInit} from "@angular/core";
import {DialogRef} from "@fundamental-ngx/core/dialog";
import {UntypedFormBuilder as FormBuilder, UntypedFormGroup as FormGroup, FormControl, Validators} from "@angular/forms";
import {firstValueFrom} from "rxjs";
import {AutomationModel} from "../../../../core/models/misc/automation.model";
import {
    AutomationType,
    WizardUtils
} from "../shared/WizardHelpers";
import {AutomationService} from "../../../../service/services/rest/automation/automation.service";
import {Const} from "../../../../core/helpers/Const";
import {CustomValidators} from "../../../../core/helpers/CustomValidators";
import {WizardSideDialogComponent} from "../../../../shared/components/wizard-side-dialog/wizard-side-dialog.component";
import {
    STEP_SETUP_FORM_CONTROLS,
    STEP_SETUP_FORM_GROUPS
} from "./unified-suite-step-setup/unified-suite-step-setup.component";

export const PAYLOAD_FORM_GROUPS = {
    PAYLOAD: "payload",
    SERVICE_PUBLISH_CONFIGURATION: "servicePublishConfiguration",
};

export const PAYLOAD_FORM_CONTROLS = {
    BASE_FOLDER_NAME: "baseFolderName",
    BASE_FOLDER_DISPLAY_NAME: "baseFolderDisplayName",
    MANAGED_SERVICE_NAME: "managedServiceName",
    RESOURCE_GROUP_NAME: "resourceGroupName",
    TEST_FOLDER_NAME: "testFolderName",
    TEST_RESOURCE_GROUP_NAME: "testResourceGroupName",

    SERVICE_PUBLISH_CONFIGURATION: {
        NAME: "name"
    }
};

@Component({
    selector: "app-unified-create-suite-side-dialog",
    templateUrl: "./unified-create-suite-dialog.component.html",
    styleUrls: ["./unified-create-suite-dialog.component.scss"],
    standalone: false
})
export class UnifiedCreateSuiteDialogComponent implements AfterViewInit {
    readonly Const = Const;
    readonly STEP_SETUP_FORM_GROUPS = STEP_SETUP_FORM_GROUPS;
    @ViewChild('dialog') private dialog: WizardSideDialogComponent;

    dialogRef = inject(DialogRef);
    _fb = inject(FormBuilder);
    _cdRef = inject(ChangeDetectorRef);
    _automationService = inject(AutomationService);

    loading = false;
    errorText: string;
    errorMsg: string;

    form = this._fb.group({});

    constructor() {
        this.initPayloadControls();
    }

    ngAfterViewInit() {
        // Override the goToStep method to intercept step changes
        if (this.dialog && this.dialog.goToStep) {
            const originalGoToStep = this.dialog.goToStep.bind(this.dialog);
            this.dialog.goToStep = (step: number) => {
                originalGoToStep(step);
                this.onStepChange(step);
            };
        }
    }

    onStepChange(stepIndex: number) {
        // When navigating to the review step (index 1), initialize tenant product type
        if (stepIndex === 1) {
            this.initializeTenantProductType();
        }
    }

    private initializeTenantProductType(): void {
        const tenantProductTypeControl = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)
            ?.get(STEP_SETUP_FORM_GROUPS.SERVICE_METADATA)
            ?.get(STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.TENANT_PRODUCT_TYPE) as FormControl;

        const suiteNameControl = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP)
            ?.get(STEP_SETUP_FORM_GROUPS.SERVICE_METADATA)
            ?.get(STEP_SETUP_FORM_CONTROLS.SERVICE_METADATA.SUITE_NAME) as FormControl;

        // If tenant product type is empty, set default value based on suite name
        if (!tenantProductTypeControl?.value && suiteNameControl?.value) {
            const suiteName = suiteNameControl.value;
            const technicalName = WizardUtils.convertToTechnicalName(suiteName, "");
            tenantProductTypeControl.setValue(`enter_suite_tpt_${technicalName}`, {emitEvent: false});
        }
    }

    initPayloadControls() {
        this.form.addControl("payload", this._fb.group({
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_NAME]: ["", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.BASE_FOLDER_DISPLAY_NAME]: [""],
            [PAYLOAD_FORM_CONTROLS.MANAGED_SERVICE_NAME]: ["", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.RESOURCE_GROUP_NAME]: ["resources", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.TEST_FOLDER_NAME]: ["test", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_CONTROLS.TEST_RESOURCE_GROUP_NAME]: ["test-resources", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]],
            [PAYLOAD_FORM_GROUPS.SERVICE_PUBLISH_CONFIGURATION]: this._fb.group({
                [PAYLOAD_FORM_CONTROLS.SERVICE_PUBLISH_CONFIGURATION.NAME]: ["", [Validators.required, Validators.maxLength(63), CustomValidators.ValidateTechnicalName]]
            })
        }));
    }

    validateForm() {
        return this.form.valid && !this.loading;
    }

    async onSubmit() {
        this.setLoading(true);

        let automationParams: AutomationModel = this.generateParams();
        try {
            const res = await firstValueFrom(this._automationService.runAutomation(automationParams));
            const returnParams = this.generateReturnParams(res);
            this.dialogRef.close(returnParams);
            this.setLoading(false);
        } catch (e: any) {
            this.errorText = "Product blueprint setup failed due to an unexpected error.";
            this.errorMsg = e.message;
            this.setLoading(false);
            this._cdRef.detectChanges();
            this.dialog.focusErrorStrip();
        }
    }

    generateReturnParams(automationResult: any) {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD).value;
        const step1 = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).getRawValue();
        return {
            res: automationResult,
            payload,
            step1
        };
    }

    generateParams() {
        const payload = this.form.get(PAYLOAD_FORM_GROUPS.PAYLOAD).value;
        const step1 = this.form.get(STEP_SETUP_FORM_GROUPS.STEP_SETUP).getRawValue();

        if(!step1.serviceMetadata.tenantProductType?.length) {
            step1.serviceMetadata.tenantProductType = `enter_suite_tpt_${step1.serviceMetadata.suiteName}`;
        }

        let automationParams: AutomationModel = {
            automationId: AutomationType.SUITE_REGISTRATION,
            parameters: {
                path: this.dialogRef.data.path,
                serviceMetadata: step1[STEP_SETUP_FORM_GROUPS.SERVICE_METADATA],
                businessTypes: step1[STEP_SETUP_FORM_CONTROLS.BUSINESS_TYPES],
                ...payload
            }
        };
        return automationParams;
    }

    setLoading(isLoading: boolean) {
        this.loading = isLoading;
        let html = `
              <div>
                <p>We're currently setting up your product blueprint and the required resources in the unified services framework.</p>
                <p>This process may take a few moments to complete.</p>
                <p><strong>Please do not close or refresh this browser page until the process has finished.</strong></p>
              </div>`;
        let busyText = $localize`:YMSG|Unified wizard loading text@@organizations.unified-create-application-dialog.loadingText:${html}`;
        this.dialog.setBusy(isLoading, busyText);
    }
}
